import React from 'react'
import { Divider, Input, Popover, PopoverSurface, PopoverTrigger, makeStyles, tokens } from '@fluentui/react-components'
import { ColorArea, ColorPicker, ColorSlider } from '@fluentui/react-color-picker'
import { TinyColor } from '@ctrl/tinycolor'
import { Checkmark12Filled, Edit12Filled } from '@fluentui/react-icons'
import { t } from '../../../i18n/i18n'
import { Control, Controller } from 'react-hook-form'
import { Field } from './Field'

const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/

// Resolved hex values of our Fluent theme's shared palette (colorPalette*BorderActive tokens in createLightTheme(Robust), src/index.tsx),
// skipping Marigold/Platinum since this app overrides those to a cream tone and a gray for other UI.
// Hardcoded (not read via `tokens`, which yields CSS var() references, not values storable as a plain hex).
const PRESET_COLORS = ['#d13438', '#da3b01', '#c19c00', '#107c10', '#038387', '#0078d4', '#881798', '#bf0077']

const useStyles = makeStyles({
   wrapper: {
      appearance: 'none',
      position: 'relative',
      width: '40px',
      height: '40px',
      padding: 0,
      margin: 0,
      border: 'none',
      background: 'none',
      cursor: 'pointer',
   },
   swatch: {
      appearance: 'none',
      width: '100%',
      height: '100%',
      padding: 0,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      borderRadius: tokens.borderRadiusMedium,
      cursor: 'pointer',
      transition: 'box-shadow 0.1s ease-in-out',
      ':hover': {
         boxShadow: tokens.shadow4,
      },
   },
   editBadge: {
      position: 'absolute',
      bottom: '-4px',
      right: '-4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '16px',
      height: '16px',
      borderRadius: tokens.borderRadiusCircular,
      backgroundColor: tokens.colorNeutralBackground1,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      color: tokens.colorNeutralForeground1,
      pointerEvents: 'none',
   },
   popoverContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
      width: '240px',
   },
   colorArea: {
      width: '100%',
      height: '160px',
   },
   colorSlider: {
      width: '100%',
   },
   hexRow: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacingHorizontalS,
   },
   previewSwatch: {
      width: '32px',
      height: '32px',
      flexShrink: 0,
      borderRadius: tokens.borderRadiusMedium,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
   },
   hexInput: {
      flexGrow: 1,
      minWidth: 0,
   },
   presetRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: tokens.spacingHorizontalXS,
   },
   preset: {
      appearance: 'none',
      padding: 0,
      width: '22px',
      height: '22px',
      borderRadius: tokens.borderRadiusMedium,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
   },
})

type ControlledColorPickerProps = {
   name: any
   control: Control<any>
   label?: string
   disabled?: boolean
   required?: boolean
}
export const ControlledColorPicker = ({ name, control, label, disabled, required }: ControlledColorPickerProps) => {
   const classes = useStyles()
   return (
      <Controller
         name={name}
         control={control}
         rules={{
            required: required ? t('RequiredField') : false,
            pattern: { value: HEX_COLOR_PATTERN, message: t('InvalidColor') },
         }}
         render={({ field: { value, onChange }, fieldState: { error }, formState: { disabled: formDisabled } }) => {
            const isDisabled = disabled || formDisabled ? true : false
            const safeHex = HEX_COLOR_PATTERN.test(value) ? value : '#ffffff'
            const hsvColor = new TinyColor(safeHex).toHsv()

            return (
               <Field label={label} required={required ?? false} validationMessage={error ? error.message : null} validationState={error ? 'error' : null}>
                  <Popover positioning="below" withArrow trapFocus>
                     <PopoverTrigger disableButtonEnhancement>
                        <button type="button" className={classes.wrapper} disabled={isDisabled} aria-label={t('ChooseColor')}>
                           <div className={classes.swatch} style={{ backgroundColor: safeHex }} />
                           {!isDisabled && (
                              <div className={classes.editBadge}>
                                 <Edit12Filled />
                              </div>
                           )}
                        </button>
                     </PopoverTrigger>
                     <PopoverSurface className={classes.popoverContent}>
                        <ColorPicker color={hsvColor} onColorChange={(_, data) => onChange(new TinyColor(data.color).toHexString())}>
                           <ColorArea className={classes.colorArea} style={{ width: '100%', height: '160px', minWidth: 0, minHeight: '160px' }} />
                           <ColorSlider className={classes.colorSlider} style={{ width: '100%', minWidth: 0 }} />
                        </ColorPicker>
                        <div className={classes.hexRow}>
                           <div className={classes.previewSwatch} style={{ backgroundColor: safeHex }} />
                           <Input className={classes.hexInput} value={value ?? ''} onChange={(e, data) => onChange(data.value)} />
                        </div>
                        <Divider />
                        <div className={classes.presetRow}>
                           {PRESET_COLORS.map((preset) => (
                              <button
                                 key={preset}
                                 type="button"
                                 className={classes.preset}
                                 style={{ backgroundColor: preset }}
                                 aria-label={preset}
                                 aria-pressed={value?.toLowerCase() === preset.toLowerCase()}
                                 onClick={() => onChange(preset)}
                              >
                                 {value?.toLowerCase() === preset.toLowerCase() && <Checkmark12Filled />}
                              </button>
                           ))}
                        </div>
                     </PopoverSurface>
                  </Popover>
               </Field>
            )
         }}
      />
   )
}
